import { Pencil, Repeat, Trash2 } from 'lucide-react'
import { cloneElement, isValidElement, useMemo, useState } from 'react'
import type { CSSProperties, ReactElement, ReactNode } from 'react'

import { useScheduleI18n } from '../../i18n'
import type { ScheduleEventId } from '../../types'
import {
  dsText,
  fontSize,
  formatDateLong,
  formatTime,
  tokenVar,
} from '../../utils'
import type { ScheduleEventHelper } from '../../utils/scheduleEvent'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { ScheduleStatusBadge } from '../ScheduleStatusBadge'

export interface OverrideItemProps {
  /** The processed schedule event helper */
  eventHelper: ScheduleEventHelper
  /** Whether editing is allowed */
  canEdit?: boolean
  /** Whether deletion is allowed */
  canDelete?: boolean
  /** Called when edit button is clicked */
  onEdit?: (eventId: ScheduleEventId) => void
  /** Called when delete button is clicked */
  onDelete?: (eventId: ScheduleEventId, eventName: string) => void
  /** Custom edit icon (defaults to built-in pencil icon) */
  editIcon?: ReactNode
  /** Custom delete icon (defaults to built-in trash icon) */
  deleteIcon?: ReactNode
}

const HolidayIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M9.981 14.811L9.514 17.537L11.963 16.25L14.412 17.537L13.944 14.811L15.926 12.879L13.188 12.481L11.963 10L10.738 12.481L8 12.879L9.981 14.811Z"
      fill="currentColor"
    />
    <path
      d="M19 4H17V2H15V4H9V2H7V4H5C3.897 4 3 4.897 3 6V20C3 21.103 3.897 22 5 22H19C20.103 22 21 21.103 21 20V6C21 4.897 20.103 4 19 4ZM19.002 20H5V8H19L19.002 20Z"
      fill="currentColor"
    />
  </svg>
)

const RepeatIcon = () => <Repeat size={14} />

const DefaultEditIcon = ({ color }: { color: string }) => (
  <Pencil size={18} color={color} />
)

const DefaultTrashIcon = ({ color }: { color: string }) => (
  <Trash2 size={18} color={color} />
)

const renderTokenizedIcon = (
  icon: ReactNode | undefined,
  fallback: ReactNode,
  color: string,
): ReactNode => {
  if (isValidElement(icon)) {
    const iconElement = icon as ReactElement<{
      color?: string
      stroke?: string
      fill?: string
      style?: CSSProperties
    }>

    return cloneElement(iconElement, {
      color,
      stroke: color,
      fill: color,
      style: {
        color,
        stroke: color,
        fill: color,
        ...(iconElement.props.style ?? {}),
      },
    })
  }

  return icon ?? fallback
}

/**
 * Individual override item component
 * Displays a single override/holiday event
 */
export const OverrideItem = ({
  eventHelper,
  canEdit = false,
  canDelete = false,
  onEdit,
  onDelete,
  editIcon,
  deleteIcon,
}: OverrideItemProps) => {
  const { text, locale } = useScheduleI18n()
  const [isEditHovered, setIsEditHovered] = useState(false)
  const [isDeleteHovered, setIsDeleteHovered] = useState(false)
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const isActive = eventHelper.isCurrentlyActive()
  const isHoliday = eventHelper.isHolidayEvent()
  const repeatText = eventHelper.repeatToText()
  const summary = eventHelper.getSummary()
  const eventId = eventHelper.getId()
  const editIconColor = tokenVar('blue.fg')
  const deleteIconColor = tokenVar('red.fg')
  const overrideInEffectLabel = text(
    'schedule.overrides.item.overrideInEffect',
    'Override in effect',
  )
  const editOverrideLabel = text(
    'schedule.overrides.item.editOverrideTitle',
    'Edit Override',
  )
  const deleteOverrideLabel = text(
    'schedule.overrides.item.deleteOverrideTitle',
    'Delete Override',
  )
  const deleteHolidayTitle = text(
    'schedule.overrides.item.deleteHolidayTitle',
    'Delete holiday',
  )
  const deleteOverrideTitle = text(
    'schedule.overrides.item.deleteOverrideModalTitle',
    'Delete override',
  )
  const repeatsPrefix = text('schedule.overrides.item.repeatsPrefix', 'Repeats')
  const closedLabel = text('schedule.common.closed', 'Closed')
  const dateRangeText = useMemo(() => {
    const start = eventHelper.zonedDateTimeStart
    const end = eventHelper.zonedDateTimeEnd
    const startDate = formatDateLong(start, locale)
    // All-day dtend values are exclusive; display the final included day.
    const displayedEnd = eventHelper.isAllDayEvent()
      ? end.subtract({ days: 1 })
      : end
    const endDate = formatDateLong(displayedEnd, locale)
    const startTime = formatTime(start, locale)
    const endTime = formatTime(end, locale)

    if (eventHelper.isSingleDayEvent()) {
      if (eventHelper.isAllDayEvent()) {
        return `${closedLabel} • ${startDate}`
      }
      return `${closedLabel} • ${startDate} ${startTime} - ${endTime}`
    }

    if (eventHelper.isAllDayEvent()) {
      return `${closedLabel} • ${startDate} - ${endDate}`
    }

    return `${closedLabel} • ${startDate} ${startTime} - ${endDate} ${endTime}`
  }, [closedLabel, eventHelper, locale])
  const rowToneStyles = isActive
    ? {
        background: tokenVar('orange.subtle'),
        border: tokenVar('orange.muted'),
      }
    : {
        background: tokenVar('gray.subtle'),
        border: tokenVar('border.default'),
      }

  const handleEditClick = () => {
    if (canEdit && onEdit) {
      onEdit(eventId)
    }
  }

  const handleDeleteClick = () => {
    if (canDelete && onDelete) {
      setIsDeleteModalOpen(true)
    }
  }

  const handleConfirmDelete = () => {
    if (canDelete && onDelete) {
      onDelete(eventId, summary)
    }
    setIsDeleteModalOpen(false)
  }

  return (
    <>
      <div
        className="override-item"
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '12px',
          padding: '14px 14px',
          backgroundColor: rowToneStyles.background,
          border: `1px solid ${rowToneStyles.border}`,
          borderRadius: '10px',
          transition: 'background-color 0.3s',
        }}
      >
        <div
          className="override-item__summary"
          style={{ flex: 1, minWidth: 0, paddingRight: '8px' }}
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              flexWrap: 'wrap',
            }}
          >
            {isHoliday && (
              <span style={{ color: tokenVar('fg.default') }}>
                <HolidayIcon />
              </span>
            )}
            <strong
              style={{
                fontFamily: dsText.Body1.fontFamily,
                fontSize: fontSize(dsText.Body1.fontSize),
                fontWeight: dsText.Body1_Bold.fontWeight,
                lineHeight: dsText.Body1.lineHeight,
                color: tokenVar('fg.default'),
              }}
            >
              {summary}
            </strong>
            {isActive && (
              <ScheduleStatusBadge
                label={overrideInEffectLabel}
                tone="override"
              />
            )}
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              marginTop: '4px',
            }}
          >
            <span
              style={{
                fontFamily: dsText.Body2.fontFamily,
                fontSize: fontSize(dsText.Body2.fontSize),
                fontWeight: dsText.Body2.fontWeight,
                lineHeight: dsText.Body2.lineHeight,
                color: tokenVar('fg.subtle'),
              }}
            >
              {dateRangeText}
            </span>
            {repeatText && (
              <span
                title={`${repeatsPrefix} ${repeatText}`}
                style={{ color: tokenVar('fg.muted'), cursor: 'help' }}
              >
                <RepeatIcon />
              </span>
            )}
          </div>
        </div>

        <div
          className="override-item__actions"
          style={{ display: 'flex', gap: '4px', marginTop: '2px' }}
        >
          <button
            type="button"
            onClick={handleEditClick}
            onMouseEnter={() => setIsEditHovered(true)}
            onMouseLeave={() => setIsEditHovered(false)}
            disabled={!canEdit}
            title={editOverrideLabel}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '30px',
              height: '30px',
              cursor: canEdit ? 'pointer' : 'not-allowed',
              opacity: canEdit ? 1 : 0.5,
              backgroundColor:
                canEdit && isEditHovered
                  ? tokenVar('blue.subtle')
                  : 'transparent',
              border: 'none',
              borderRadius: '6px',
              color: editIconColor,
              transition: 'background-color 0.2s',
            }}
          >
            {renderTokenizedIcon(
              editIcon,
              <DefaultEditIcon color={editIconColor} />,
              editIconColor,
            )}
          </button>
          {canDelete && (
            <button
              type="button"
              onClick={handleDeleteClick}
              onMouseEnter={() => setIsDeleteHovered(true)}
              onMouseLeave={() => setIsDeleteHovered(false)}
              title={deleteOverrideLabel}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '30px',
                height: '30px',
                cursor: 'pointer',
                backgroundColor: isDeleteHovered
                  ? tokenVar('red.subtle')
                  : 'transparent',
                border: 'none',
                borderRadius: '6px',
                color: deleteIconColor,
                transition: 'background-color 0.2s',
              }}
            >
              {renderTokenizedIcon(
                deleteIcon,
                <DefaultTrashIcon color={deleteIconColor} />,
                deleteIconColor,
              )}
            </button>
          )}
        </div>
      </div>
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title={isHoliday ? deleteHolidayTitle : deleteOverrideTitle}
        itemName={summary}
      />
    </>
  )
}
